<template>
  <Modal
    v-model="dialogVisible"
    :class-name="modalClassName"
    :title="dialogTitle"
    width="980"
    :mask-closable="false"
    :before-close="handleBeforeModalClose"
  >
    <div
      class="profile-dialog-body-wrap"
      :class="{
        'profile-dialog-body-wrap--edit-info': isEditMode && activeTab === 'info',
        'profile-dialog-body-wrap--edit-episodes': isEditMode && activeTab === 'episodes',
        'profile-dialog-body-wrap--episodes-tab': activeTab === 'episodes',
        'profile-dialog-body-wrap--info-tab': activeTab === 'info',
        'profile-dialog-body-wrap--has-reject': hasRejectReason
      }"
    >
      <Spin v-if="materialsLoading" fix size="large" />
      <div
        v-if="hasRejectReason"
        class="profile-reject-banner"
        :class="isEditMode ? 'profile-reject-banner--edit' : 'profile-reject-banner--view'"
      >
        <template v-if="isEditMode">
          <div class="profile-reject-banner-title">
            {{ rejectVersionLabel }} 驳回原因
          </div>
          <div class="profile-reject-banner-text">{{ rejectReasonText }}</div>
        </template>
        <p v-else class="profile-reject-banner-view-text">
          <span class="profile-reject-banner-view-label">驳回原因：</span>{{ rejectReasonText }}
        </p>
      </div>

      <p v-if="!isEditMode" class="profile-dialog-tip">
        当前送审资料。修改后确认送审；未提交的修改不会影响当前审核或线上版本。
      </p>

      <Tabs v-model="activeTab" class="profile-dialog-tabs">
      <TabPane label="剧目信息" name="info">
        <div
          class="profile-tabs-panel"
          :class="{ 'profile-tabs-panel--edit-info': isEditMode }"
        >
        <div class="profile-info-panel" :class="{ 'profile-info-panel--no-scroll': isEditMode }">
          <template v-if="!isEditMode">
            <Row :gutter="32">
              <Col :span="12">
                <div class="profile-field">
                  <div class="profile-field-label">短剧名称</div>
                  <div class="profile-field-value">{{ displayProfile.playName }}</div>
                </div>
              </Col>
              <Col :span="12">
                <div class="profile-field">
                  <div class="profile-field-label">主剧语言</div>
                  <div class="profile-field-value">{{ displayProfile.langName }}</div>
                </div>
              </Col>
            </Row>
            <Row :gutter="32" class="profile-field-row">
              <Col :span="12">
                <div class="profile-field">
                  <div class="profile-field-label">短剧集数</div>
                  <div class="profile-field-value">{{ displayProfile.episodeCount }}</div>
                </div>
              </Col>
              <Col :span="12">
                <div class="profile-field">
                  <div class="profile-field-label">出品年份</div>
                  <div class="profile-field-value">{{ displayProfile.productionYear }}</div>
                </div>
              </Col>
            </Row>
            <div class="profile-field profile-field-row">
              <div class="profile-field-label">送审剧类型</div>
              <div class="profile-field-value">{{ displayProfile.submitType }}</div>
            </div>
            <div class="profile-field profile-field-row">
              <div class="profile-field-label">TT 题材标签</div>
              <div class="profile-field-value">{{ displayProfile.genreTag }}</div>
            </div>
            <div class="profile-field profile-field-row">
              <div class="profile-field-label">剧目简介</div>
              <div class="profile-field-value profile-field-value--multiline">
                {{ displayProfile.synopsis }}
              </div>
            </div>
            <div class="profile-field profile-field-row">
              <div class="profile-field-label">剧目封面</div>
              <div class="profile-field-value">
                {{ displayProfile.coverUrl || displayProfile.coverFileName }}
              </div>
            </div>
          </template>

          <Form
            v-else
            ref="profileEditForm"
            :model="editForm"
            :rules="profileEditRules"
            class="profile-edit-form"
          >
            <Row :gutter="32">
              <Col :span="12">
                <FormItem prop="playName" class="profile-form-item">
                  <div class="profile-field">
                    <div class="profile-field-label">
                      短剧名称（title）<span class="profile-required">*</span>
                    </div>
                    <Input
                      v-model="editForm.playName"
                      :maxlength="80"
                      show-word-limit
                      placeholder="请输入短剧名称（最多 80 字）"
                    />
                  </div>
                </FormItem>
              </Col>
              <Col :span="12">
                <div class="profile-field">
                  <div class="profile-field-label">
                    主剧语言<span class="profile-label-hint">· 随主剧变更，不可修改</span>
                  </div>
                  <Input
                    :value="displayProfile.langName"
                    disabled
                    readonly
                    class="profile-input-readonly"
                  />
                </div>
              </Col>
            </Row>
            <Row :gutter="32" class="profile-field-row">
              <Col :span="12">
                <div class="profile-field">
                  <div class="profile-field-label">
                    短剧集数<span class="profile-label-hint">· 系统维护，不可修改</span>
                  </div>
                  <Input
                    :value="displayEpisodeCountText"
                    disabled
                    readonly
                    class="profile-input-readonly"
                  />
                </div>
              </Col>
              <Col :span="12">
                <div class="profile-field">
                  <div class="profile-field-label">
                    出品年份<span class="profile-label-hint">· 系统映射，不可修改</span>
                  </div>
                  <Input
                    :value="systemMappedProductionYear"
                    disabled
                    readonly
                    class="profile-input-readonly"
                  />
                </div>
              </Col>
            </Row>
            <div class="profile-field profile-field-row">
              <div class="profile-field-label">
                送审剧类型<span class="profile-label-hint">· 系统映射，不可修改</span>
              </div>
              <Input
                :value="systemMappedSubmitType"
                disabled
                readonly
                class="profile-input-readonly profile-input-readonly--block"
              />
            </div>
            <div class="profile-field profile-field-row">
              <div class="profile-field-label">
                题材标签<span class="profile-label-hint">· 系统映射，不可修改</span>
              </div>
              <Input
                :value="systemMappedGenreTag"
                disabled
                readonly
                class="profile-input-readonly profile-input-readonly--block"
              />
            </div>
            <FormItem prop="synopsis" class="profile-form-item">
              <div class="profile-field profile-field-row">
                <div class="profile-field-label">
                  剧目简介（desp）<span class="profile-required">*</span>
                </div>
                <Input
                  v-model="editForm.synopsis"
                  type="textarea"
                  :rows="3"
                  :maxlength="500"
                  show-word-limit
                  placeholder="请输入剧目简介（最多 500 字）"
                />
              </div>
            </FormItem>
            <FormItem prop="coverUrl" class="profile-form-item">
              <div class="profile-field profile-field-row profile-cover-list-field">
                <div class="profile-field-label">
                  剧目封面（cover_list）<span class="profile-required">*</span>
                </div>
                <Input
                  v-model="editForm.coverUrl"
                  placeholder="封面链接/文件名，或通过下方选择替换文件"
                />
                <input
                  ref="coverFileInput"
                  type="file"
                  class="profile-cover-file-input"
                  accept="image/jpeg,image/jpg,image/png,image/webp"
                  @change="handleCoverFileChange"
                />
                <Button
                  type="primary"
                  ghost
                  class="profile-cover-upload-btn"
                  @click="handleSelectCover"
                >
                  选择替换文件
                </Button>
              </div>
            </FormItem>
          </Form>
        </div>
        </div>
      </TabPane>
      <TabPane :label="episodeTabLabel" name="episodes">
        <div
          class="profile-tabs-panel profile-tabs-panel--episodes"
          :class="{ 'profile-tabs-panel--edit-episodes': isEditMode }"
        >
          <SingleList
            ref="singleList"
            :episodes="materialEpisodes"
            :subtitle-language-defs="subtitleLanguageDefs"
            :editable="isEditMode"
          />
        </div>
      </TabPane>
      </Tabs>
    </div>

    <template #footer>
      <template v-if="!isEditMode">
        <Button @click="handleClose">关闭</Button>
        <Button
          v-if="canModifySubmitProfile"
          type="primary"
          :disabled="materialsLoading"
          @click="enterEditMode"
        >
          修改送审资料
        </Button>
      </template>
      <template v-else>
        <Button @click="handleCancelEdit">取消</Button>
        <Button
          type="primary"
          :loading="submitLoading"
          @click="handleConfirmSubmit"
        >
          {{ hasRejectReason ? '确认重新送审' : '确认送审' }}
        </Button>
      </template>
    </template>
  </Modal>
</template>

<script>
import {
  getTtMediaReviewMaterials,
  postTtMediaReviewResubmit
} from '@/api/short-review'
import {
  buildResubmitPayload,
  explainResubmitFailure,
  hasResubmitPayloadChanges,
  validateResubmitPayload
} from '../../utils/resubmitPayload'
import { uploadReviewMaterialFile } from '../../utils/reviewFileUpload'
import SingleList, { extractSubtitleLanguageDefs } from './singleList.vue'

function formatDisplayValue(value, emptyText = '—') {
  if (value == null || value === '') return emptyText
  return value
}

function formatGenreTag(tagNames) {
  const list = (tagNames || []).filter((item) => String(item || '').trim())
  return list.length ? list.join('、') : '—'
}

export default {
  name: 'ViewProfileDialog',
  components: {
    SingleList
  },
  props: {
    visible: {
      type: Boolean,
      default: false
    },
    row: {
      type: Object,
      default: () => ({})
    },
    /** 打开时直接进入修改送审（如从审核记录进入） */
    defaultEditMode: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      dialogVisible: this.visible,
      activeTab: 'info',
      isEditMode: false,
      editForm: {},
      savedProfile: {},
      materialsRaw: null,
      materialsLoading: false,
      submitLoading: false,
      coverUploading: false,
      // 【临时】弹窗视口垂直居中，见下方 methods 中「弹窗垂直居中」区块；验收不理想可整段删除
      _modalResizeObserver: null,
      _modalCenterTimer: null,
      _lastModalTop: null,
      _modalCenterSettled: false,
      /** 防止右上角 × 的 input 与 on-cancel 重复弹出放弃修改确认 */
      _pendingUnsavedCloseConfirm: false
    }
  },
  computed: {
    materialEpisodes() {
      return (this.materialsRaw && this.materialsRaw.episodes) || []
    },
    /** 送审资料接口返回的外挂字幕语言列表（用于单集编辑表单动态渲染） */
    subtitleLanguageDefs() {
      return extractSubtitleLanguageDefs(this.materialsRaw)
    },
    displayProfile() {
      const row = this.row || {}
      const album = (this.materialsRaw && this.materialsRaw.album) || {}
      const episodeCountFromApi =
        album.seqNum != null
          ? album.seqNum
          : this.materialEpisodes.length
      const base = {
        playName: formatDisplayValue(album.title || row.playName),
        langName: formatDisplayValue(album.languageName || album.language),
        episodeCount: episodeCountFromApi,
        productionYear: album.year != null && album.year !== '' ? album.year : '—',
        submitType: formatDisplayValue(album.dramaTypeName),
        genreTag: formatGenreTag(album.tagNames),
        synopsis: formatDisplayValue(
          album.desp || album.desc || album.description
        ),
        coverFileName: '',
        coverUrl: album.coverUrl || ''
      }
      return { ...base, ...this.savedProfile }
    },
    episodeTabLabel() {
      return `单集列表 (${this.displayProfile.episodeCount})`
    },
    modalClassName() {
      const base = 'view-profile-dialog'
      if (!this.isEditMode) return base
      if (this.activeTab === 'episodes') {
        return `${base} view-profile-dialog--edit view-profile-dialog--edit-episodes-tab`
      }
      return `${base} view-profile-dialog--edit view-profile-dialog--edit-info-tab`
    },
    dialogTitle() {
      const prefix = this.isEditMode ? '修改送审资料' : '查看资料'
      // 标题用列表主剧名称 playName，不用 materials 的 album.title / savedProfile（避免与送审 title、简介混淆）
      const name = this.row && this.row.playName
      const text =
        name != null && String(name).trim() !== '' ? String(name).trim() : ''
      return text ? `${prefix} · ${text}` : prefix
    },
    rejectReasonText() {
      const row = this.row || {}
      return (row.rejectReason || '').trim()
    },
    rejectVersionLabel() {
      const row = this.row || {}
      return (row.rejectVersion || 'V4').trim()
    },
    hasRejectReason() {
      return !!this.rejectReasonText
    },
    systemMappedProductionYear() {
      const v = this.displayProfile.productionYear
      return v != null && v !== '' ? String(v) : '—'
    },
    systemMappedSubmitType() {
      return this.displayProfile.submitType || '—'
    },
    systemMappedGenreTag() {
      return this.displayProfile.genreTag || '—'
    },
    displayEpisodeCountText() {
      const n = this.displayProfile.episodeCount
      if (n == null || n === '') return '—'
      return String(n)
    },
    /** 接口 reviewStatus：1 为审核中，不可修改送审资料 */
    reviewStatusApiCode() {
      const row = this.row || {}
      if (row.reviewStatusApi != null) return Number(row.reviewStatusApi)
      if (typeof row.reviewStatus === 'number') return row.reviewStatus
      return null
    },
    /** 审核中(1)或申诉中(5)不可修改送审资料 */
    isReviewInProgress() {
      return this.reviewStatusApiCode === 1 || this.reviewStatusApiCode === 5
    },
    /** 查看态是否展示「修改送审资料」（审核中或接口不可编辑时仅关闭） */
    canModifySubmitProfile() {
      if (this.materialsLoading || !this.materialsRaw) return false
      if (this.materialsRaw.editable === false) return false
      return !this.isReviewInProgress
    },
    profileEditRules() {
      return {
        playName: [
          {
            validator: this.validateProfilePlayName,
            trigger: 'blur'
          }
        ],
        synopsis: [
          {
            validator: this.validateProfileSynopsis,
            trigger: 'blur'
          }
        ],
        coverUrl: [
          {
            validator: this.validateProfileCoverUrl,
            trigger: 'blur'
          }
        ]
      }
    }
  },
  watch: {
    visible(val) {
      this.dialogVisible = val
      if (val) {
        this.activeTab = 'info'
        this.isEditMode = false
        this.savedProfile = {}
        this.fetchMaterials().then(() => {
          if (this.defaultEditMode && this.canModifySubmitProfile) {
            this.$nextTick(() => {
              this.enterEditMode()
            })
          }
        })
      } else {
        this.materialsRaw = null
        this.materialsLoading = false
      }
    },
    dialogVisible(val) {
      this.$emit('update:visible', val)
      // 【临时】弹窗垂直居中：打开/关闭时绑定监听
      if (val) {
        this._modalCenterSettled = false
        this.$nextTick(() => {
          this.bindModalVerticalCenter()
        })
      } else {
        this.unbindModalVerticalCenter()
      }
    },
    isEditMode() {
      if (!this.dialogVisible) return
      // 【临时】编辑态高度会变，重新绑定监听；查看态只居中一次
      this._modalCenterSettled = false
      this.bindModalVerticalCenter()
    },
    activeTab() {
      if (!this.dialogVisible || !this.isEditMode) return
      // 【临时】仅编辑态切 Tab 需要重新居中
      this._modalCenterSettled = false
      this.scheduleModalVerticalCenter(200)
    }
  },
  created() {
    // 【临时】弹窗垂直居中
    this._onWindowResizeForModal = () => {
      this._modalCenterSettled = false
      this.scheduleModalVerticalCenter(80)
    }
  },
  beforeDestroy() {
    // 【临时】弹窗垂直居中
    this.unbindModalVerticalCenter()
    clearTimeout(this._modalCenterTimer)
  },
  methods: {
    /*
     * 【临时方案】弹窗在视口内垂直居中
     * iView Modal 默认 top 固定，内容高度变化时会「向下长」而不居中。
     * 现用 JS 动态写 top + ResizeObserver；样式见文件末尾 .ivu-modal-wrap.view-profile-dialog。
     * 若后续体验/兼容性不行，删除本区块及 data 中 _modal* 字段、watch/created/beforeDestroy 内相关调用、
     * 以及样式块「弹窗垂直居中（临时）」即可恢复默认定位。
     */
    getProfileModalElement() {
      const wraps = document.querySelectorAll('.ivu-modal-wrap.view-profile-dialog')
      for (let i = 0; i < wraps.length; i += 1) {
        const wrap = wraps[i]
        if (wrap.style.display === 'none') continue
        const modal = wrap.querySelector('.ivu-modal')
        if (modal) return modal
      }
      return null
    },
    shouldObserveModalResize() {
      // 查看态 body 高度固定，无需持续监听（避免打开时 top 被反复改写而「浮动」）
      return this.isEditMode
    },
    syncModalVerticalCenter() {
      if (!this.dialogVisible) return
      if (this._modalCenterSettled && !this.shouldObserveModalResize()) return
      const modal = this.getProfileModalElement()
      if (!modal) return
      const modalHeight = modal.offsetHeight
      if (!modalHeight) return
      const vh = window.innerHeight
      const minMargin = 24
      let top = Math.round((vh - modalHeight) / 2)
      if (top < minMargin) top = minMargin
      if (this._lastModalTop != null && Math.abs(this._lastModalTop - top) <= 4) {
        if (!this.shouldObserveModalResize()) {
          this._modalCenterSettled = true
        }
        return
      }
      this._lastModalTop = top
      modal.style.top = `${top}px`
      if (!this.shouldObserveModalResize()) {
        this._modalCenterSettled = true
      }
    },
    scheduleModalVerticalCenter(delay = 120) {
      if (!this.dialogVisible) return
      if (this._modalCenterSettled && !this.shouldObserveModalResize()) return
      clearTimeout(this._modalCenterTimer)
      this._modalCenterTimer = setTimeout(() => {
        this.$nextTick(() => {
          requestAnimationFrame(() => this.syncModalVerticalCenter())
        })
      }, delay)
    },
    bindModalVerticalCenter() {
      this.unbindModalVerticalCenter()
      const modal = this.getProfileModalElement()
      // 查看态：header 77 + body 590 + footer 66 = 733px，用 CSS calc 居中，避免 JS 改 top 造成打开时抖动
      if (!this.shouldObserveModalResize()) {
        if (modal) modal.style.removeProperty('top')
        return
      }
      window.addEventListener('resize', this._onWindowResizeForModal)
      if (!modal) {
        this.scheduleModalVerticalCenter(100)
        return
      }
      if (typeof ResizeObserver !== 'undefined') {
        this._modalResizeObserver = new ResizeObserver(() => {
          this.scheduleModalVerticalCenter(200)
        })
        this._modalResizeObserver.observe(modal)
      }
      this.scheduleModalVerticalCenter(120)
    },
    unbindModalVerticalCenter() {
      if (this._modalResizeObserver) {
        this._modalResizeObserver.disconnect()
        this._modalResizeObserver = null
      }
      window.removeEventListener('resize', this._onWindowResizeForModal)
      this._lastModalTop = null
      this._modalCenterSettled = false
    },
    /** 编辑态放弃未提交修改前的二次确认（取消按钮与右上角 × 共用） */
    confirmDiscardUnsavedEdit(onOk, onCancel) {
      this.$Modal.confirm({
        title: '提示',
        content: '当前修改尚未提交，确定放弃修改吗？',
        okText: '确定',
        cancelText: '取消',
        onOk,
        onCancel: onCancel || (() => {})
      })
    },
    /**
     * 右上角 × / Esc：在 Modal 改内部 visible 之前拦截。
     * 有未保存改动则 reject，主弹窗保持打开并在其上弹出确认。
     */
    handleBeforeModalClose() {
      if (!this.isEditMode || !this.hasUnsavedEditChanges()) {
        return undefined
      }
      if (this._pendingUnsavedCloseConfirm) {
        return new Promise(() => {})
      }
      this._pendingUnsavedCloseConfirm = true
      return new Promise((resolve, reject) => {
        this.confirmDiscardUnsavedEdit(
          () => {
            this._pendingUnsavedCloseConfirm = false
            this.discardEditMode()
            reject(new Error('discard-edit'))
          },
          () => {
            this._pendingUnsavedCloseConfirm = false
            reject(new Error('cancel-discard'))
          }
        )
      })
    },
    finishCloseDialog() {
      if (!this.dialogVisible) return
      this.isEditMode = false
      this.dialogVisible = false
      this.$emit('close')
    },
    handleClose() {
      this.finishCloseDialog()
    },
    resolveTtVersionQuery() {
      const row = this.row || {}
      if (row.ttVersion != null && row.ttVersion !== '') {
        return Number(row.ttVersion)
      }
      if (row.reviewTtVersion != null && row.reviewTtVersion !== '') {
        return Number(row.reviewTtVersion)
      }
      const versionText = row.rejectVersion
      if (versionText == null || versionText === '') return undefined
      if (typeof versionText === 'number') return versionText
      const matched = String(versionText).trim().match(/^V?(\d+)$/i)
      return matched ? Number(matched[1]) : undefined
    },
    fetchMaterials() {
      const id = this.row && this.row.id
      if (id == null || id === '') {
        this.materialsRaw = null
        this.$Message.error('缺少审核记录，无法加载送审资料')
        return Promise.resolve()
      }
      this.materialsLoading = true
      this.materialsRaw = null
      const params = {}
      const ttVersion = this.resolveTtVersionQuery()
      if (ttVersion != null && !Number.isNaN(ttVersion)) {
        params.ttVersion = ttVersion
      }
      return getTtMediaReviewMaterials(id, params)
        .then((res) => {
          if (res.code === 200 && res.data) {
            this.materialsRaw = res.data
          } else {
            this.$Message.error(res.message || '获取送审资料失败')
          }
        })
        .catch(() => {
          this.$Message.error('获取送审资料失败')
        })
        .finally(() => {
          this.materialsLoading = false
        })
    },
    buildEditForm(source) {
      const album = (this.materialsRaw && this.materialsRaw.album) || {}
      return {
        playName: (source.playName === '—' ? '' : source.playName || '').slice(
          0,
          80
        ),
        langName: source.langName === '—' ? '' : source.langName,
        episodeCount: source.episodeCount,
        productionYear: source.productionYear,
        submitType: source.submitType,
        genreTag: source.genreTag,
        synopsis: (source.synopsis === '—' ? '' : source.synopsis || '').slice(
          0,
          500
        ),
        coverUrl: source.coverUrl || source.coverFileName || '',
        coverFileId: album.coverFileId,
        coverFile: null
      }
    },
    resolveResubmitTtVersion() {
      const fromMaterials = this.materialsRaw || {}
      const candidates = [
        fromMaterials.reviewTtVersion,
        fromMaterials.currentTtVersion,
        this.resolveTtVersionQuery()
      ]
      for (let i = 0; i < candidates.length; i += 1) {
        const n = Number(candidates[i])
        if (!Number.isNaN(n) && n > 0) return n
      }
      return undefined
    },
    parseEpisodeNoFromReject(text) {
      const match = String(text || '').match(/第(\d+)集/)
      if (!match) return null
      const no = parseInt(match[1], 10)
      return Number.isFinite(no) && no > 0 ? no : null
    },
    focusRejectEpisode() {
      const episodeNo = this.parseEpisodeNoFromReject(this.rejectReasonText)
      if (!episodeNo) {
        this.activeTab = 'info'
        return
      }
      this.activeTab = 'episodes'
      this.$nextTick(() => {
        this.$nextTick(() => {
          const list = this.$refs.singleList
          if (!list) return
          list.expandEpisode(episodeNo)
          list.scrollToEpisode(episodeNo)
        })
      })
    },
    enterEditMode() {
      if (!this.canModifySubmitProfile) return
      this.editForm = this.buildEditForm(this.displayProfile)
      this.isEditMode = true
      this.$nextTick(() => {
        const formRef = this.$refs.profileEditForm
        if (formRef && formRef.clearValidate) {
          formRef.clearValidate()
        }
      })
      if (this.rejectReasonText) {
        this.$nextTick(() => {
          this.focusRejectEpisode()
        })
      } else {
        this.activeTab = 'info'
      }
    },
    validateProfilePlayName(rule, value, callback) {
      const text = String(value == null ? '' : value).trim()
      if (!text) {
        callback(new Error('请输入短剧名称'))
        return
      }
      if (text.length > 80) {
        callback(new Error('短剧名称最多 80 字'))
        return
      }
      callback()
    },
    validateProfileSynopsis(rule, value, callback) {
      const text = String(value == null ? '' : value).trim()
      if (!text) {
        callback(new Error('请填写剧目简介'))
        return
      }
      callback()
    },
    validateProfileCoverUrl(rule, value, callback) {
      if (this.editForm.coverFile) {
        callback()
        return
      }
      const fileId = Number(this.editForm.coverFileId)
      if (!Number.isNaN(fileId) && fileId > 0) {
        callback()
        return
      }
      const text = String(value == null ? '' : value).trim()
      if (!text || text === '—') {
        callback(new Error('请填写封面链接或选择替换文件'))
        return
      }
      callback()
    },
    validateProfileEditForm() {
      const formRef = this.$refs.profileEditForm
      if (!formRef) return Promise.resolve(true)
      return new Promise((resolve) => {
        formRef.validate((valid) => resolve(valid))
      })
    },
    /** 编辑态是否存在未提交的改动（含待上传文件、可送审参数及表单差异） */
    hasUnsavedEditChanges() {
      if (!this.isEditMode) return false
      const singleList = this.$refs.singleList
      if (this.editForm.coverFile) return true
      if (
        singleList &&
        singleList.hasPendingUploads &&
        singleList.hasPendingUploads()
      ) {
        return true
      }
      if (this.materialsRaw) {
        const editedEpisodes = singleList ? singleList.getEpisodeList() : []
        const payload = buildResubmitPayload({
          materialsRaw: this.materialsRaw,
          editForm: this.editForm,
          editedEpisodes,
          ttVersion: this.resolveResubmitTtVersion()
        })
        if (hasResubmitPayloadChanges(payload)) return true
      }
      const baseline = this.buildEditForm(this.displayProfile)
      const compareFields = ['playName', 'synopsis', 'coverUrl']
      for (let i = 0; i < compareFields.length; i += 1) {
        const key = compareFields[i]
        const cur = String(this.editForm[key] == null ? '' : this.editForm[key]).trim()
        const base = String(baseline[key] == null ? '' : baseline[key]).trim()
        if (cur !== base) return true
      }
      return false
    },
    discardEditMode() {
      this.isEditMode = false
      this.activeTab = 'info'
      this.editForm = this.buildEditForm(this.displayProfile)
    },
    handleCancelEdit() {
      if (!this.hasUnsavedEditChanges()) {
        this.discardEditMode()
        return
      }
      this.confirmDiscardUnsavedEdit(() => {
        this.discardEditMode()
      })
    },
    async handleConfirmSubmit() {
      if (this.submitLoading) return
      const id = this.row && this.row.id
      if (id == null || id === '') {
        this.$Message.error('缺少审核记录，无法送审')
        return
      }
      const singleList = this.$refs.singleList
      if (this.coverUploading) {
        this.$Message.warning('封面上传中，请稍候')
        return
      }
      if (singleList && singleList.hasEpisodeUploading && singleList.hasEpisodeUploading()) {
        this.$Message.warning('单集文件上传中，请稍候')
        return
      }

      const formValid = await this.validateProfileEditForm()
      if (!formValid) {
        this.activeTab = 'info'
        return
      }

      if (singleList && singleList.validateRequiredEpisodeFields) {
        const episodeCheck = singleList.validateRequiredEpisodeFields()
        if (!episodeCheck.valid) {
          this.activeTab = 'episodes'
          this.$Message.warning(
            episodeCheck.message || '请完善单集列表必填项'
          )
          this.$nextTick(() => {
            if (episodeCheck.episodeNo != null && singleList.expandEpisode) {
              singleList.expandEpisode(episodeCheck.episodeNo)
              singleList.scrollToEpisode(episodeCheck.episodeNo)
            }
          })
          return
        }
      }

      this.submitLoading = true
      try {
        if (this.editForm.coverFile) {
          this.coverUploading = true
          const { fileId, fileUrl } = await uploadReviewMaterialFile(
            this.editForm.coverFile
          )
          this.$set(this.editForm, 'coverFileId', fileId)
          if (fileUrl) {
            this.$set(this.editForm, 'coverUrl', fileUrl)
          }
          this.editForm.coverFile = null
        }
        if (singleList && singleList.uploadPendingFiles) {
          await singleList.uploadPendingFiles()
        }

        const editedEpisodes = singleList ? singleList.getEpisodeList() : []
        const payload = buildResubmitPayload({
          materialsRaw: this.materialsRaw,
          editForm: this.editForm,
          editedEpisodes,
          ttVersion: this.resolveResubmitTtVersion()
        })

        if (!hasResubmitPayloadChanges(payload)) {
          this.$Message.warning(
            '未检测到可提交的修改（文本变更或已选替换文件并上传获得 fileId 后才会写入参数）'
          )
          return
        }

        const validateMsg = validateResubmitPayload(payload)
        if (validateMsg) {
          if (validateMsg.indexOf('剧目简介') >= 0) {
            this.activeTab = 'info'
            this.$nextTick(() => {
              const formRef = this.$refs.profileEditForm
              if (formRef) formRef.validateField('synopsis')
            })
          } else {
            this.$Message.error(validateMsg)
          }
          return
        }

        const axiosRes = await postTtMediaReviewResubmit(id, payload)
        const res = (axiosRes && axiosRes.data) || axiosRes || {}
        if (res.code === 200) {
          const taskId =
            res.data && res.data.taskId ? String(res.data.taskId) : ''
          this.$Message.success(
            taskId
              ? `送审已受理，任务 ID：${taskId}`
              : res.message || '送审已受理'
          )
          const coverUrl = (this.editForm.coverUrl || '').trim()
          this.savedProfile = {
            ...this.editForm,
            coverUrl,
            coverFileName: coverUrl,
            productionYear: this.displayProfile.productionYear,
            submitType: this.displayProfile.submitType,
            genreTag: this.displayProfile.genreTag
          }
          if (singleList) {
            singleList.commitEditSnapshot()
          }
          this.isEditMode = false
          this.$emit('submit', {
            ...this.displayProfile,
            episodes: editedEpisodes,
            task: res.data
          })
          this.dialogVisible = false
          return
        }
        const failText = explainResubmitFailure(res.message, payload)
        this.$Message.error({
          render: (h) =>
            h(
              'div',
              { style: { textAlign: 'left', whiteSpace: 'pre-wrap' } },
              failText.split('\n').map((line) => h('div', { key: line }, line))
            ),
          duration: 10
        })
      } catch (err) {
        const msg = explainResubmitFailure(
          (err && err.message) ||
            (err && err.data && err.data.message) ||
            (typeof err === 'string' ? err : ''),
          null
        )
        this.$Message.error({
          content: msg || '送审失败',
          duration: 8
        })
      } finally {
        this.coverUploading = false
        this.submitLoading = false
      }
    },
    handleSelectCover() {
      const input = this.$refs.coverFileInput
      if (!input) return
      input.value = ''
      input.click()
    },
    handleCoverFileChange(event) {
      const file = event.target.files && event.target.files[0]
      if (!file) return
      this.editForm.coverFile = file
      this.$set(this.editForm, 'coverUrl', `待上传：${file.name}`)
      this.$Message.success('已选择封面，确认送审时将上传并写入 coverFileId')
      this.$nextTick(() => {
        const formRef = this.$refs.profileEditForm
        if (formRef) formRef.validateField('coverUrl')
      })
    }
  }
}
</script>

<style scoped>
.profile-dialog-body-wrap {
  position: relative;
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 0;
}

.profile-dialog-body-wrap--edit-info {
  height: auto;
}

.profile-dialog-tip {
  flex: 0 0 auto;
  margin: 0 0 16px;
  font-size: 13px;
  line-height: 1.6;
  color: #909399;
}

.profile-reject-banner {
  flex: 0 0 auto;
  margin: 0 0 16px;
  padding: 14px 16px;
  background: #fef0f0;
  border: 1px solid #de5a50;
  border-radius: 4px;
}

.profile-reject-banner-title {
  font-size: 14px;
  font-weight: 700;
  line-height: 22px;
  color: #de5a50;
}

.profile-reject-banner-text {
  font-size: 13px;
  line-height: 1.6;
  color: #de5a50;
  word-break: break-word;
}

/* 查看态：单行说明文案，字号与灰色说明一致 */
.profile-reject-banner--view {
  padding: 10px 14px;
  background: #fef0f0;
}

.profile-reject-banner-view-text {
  margin: 0;
  font-size: 13px;
  font-weight: 400;
  line-height: 1.6;
  color: #de5a50;
  word-break: break-word;
}

.profile-reject-banner-view-label {
  color: #de5a50;
  font-weight: 500;
}

.profile-dialog-body-wrap .profile-dialog-tabs {
  flex: 1 1 auto;
  min-height: 0;
}

/* 编辑 · 剧目信息：Tabs 内容随表单高度，不被另一 Tab 的固定高度撑开 */
.profile-dialog-body-wrap--edit-info .profile-dialog-tabs {
  flex: 0 0 auto;
}

.profile-dialog-body-wrap--edit-info .profile-dialog-tabs >>> .ivu-tabs-content {
  height: auto !important;
  min-height: 0 !important;
}

.profile-dialog-body-wrap--edit-info .profile-dialog-tabs >>> .ivu-tabs-tabpane {
  height: auto !important;
}

.profile-dialog-body-wrap--edit-info .profile-tabs-panel--edit-info {
  height: auto !important;
  overflow: visible;
}

.profile-dialog-body-wrap--edit-info .profile-tabs-panel:not(.profile-tabs-panel--edit-info) {
  height: 0 !important;
  overflow: hidden;
  margin: 0;
  padding: 0;
}

.profile-dialog-body-wrap--edit-episodes .profile-tabs-panel:not(.profile-tabs-panel--episodes) {
  height: 0 !important;
  overflow: hidden;
  margin: 0;
  padding: 0;
}

.profile-dialog-body-wrap--episodes-tab
  .profile-tabs-panel:not(.profile-tabs-panel--episodes) {
  height: 0 !important;
  overflow: hidden;
  margin: 0;
  padding: 0;
}

.profile-dialog-body-wrap--info-tab .profile-tabs-panel--episodes {
  height: 0 !important;
  overflow: hidden;
  margin: 0;
  padding: 0;
}

.profile-tabs-panel--episodes {
  display: flex;
  flex-direction: column;
  min-height: 0;
  overflow: hidden;
  box-sizing: border-box;
}

.profile-dialog-tabs >>> .ivu-tabs-bar {
  margin-bottom: 20px;
}

.profile-field-row {
  margin-top: 20px;
}

.profile-field-label {
  margin-bottom: 8px;
  font-size: 13px;
  line-height: 1.4;
  color: #909399;
}

.profile-field-value {
  font-size: 15px;
  line-height: 1.5;
  color: #303133;
  word-break: break-word;
}

.profile-field-value--multiline {
  max-width: 100%;
}

.profile-cover-link {
  font-size: 15px;
  color: #2d8cf0;
  cursor: pointer;
}

.profile-cover-link:hover {
  color: #57a3f3;
}

/* Tab 内容区固定高度，避免 iView Tabs 在 flex 下高度塌陷为 0 */
.profile-tabs-panel {
  height: 462px;
  overflow: hidden;
}

.profile-tabs-panel--edit-info {
  height: auto;
  min-height: 0;
  overflow: visible;
}

.profile-info-panel {
  height: 100%;
  overflow-x: hidden;
  overflow-y: auto;
  padding-right: 4px;
}

.profile-info-panel--no-scroll {
  height: auto;
  min-height: 0;
  overflow: visible;
}

.profile-required {
  margin-left: 2px;
  color: #ed4014;
}

.profile-label-hint {
  color: #909399;
  font-weight: normal;
}

.profile-edit-form >>> .profile-form-item.ivu-form-item {
  margin-bottom: 0;
}

.profile-edit-form >>> .profile-form-item .ivu-form-item-content {
  line-height: inherit;
}

.profile-edit-form >>> .profile-form-item .ivu-form-item-error-tip {
  position: relative;
  padding-top: 4px;
  padding-bottom: 2px;
  font-size: 12px;
  line-height: 1.4;
}

.profile-edit-form >>> .ivu-input {
  font-size: 14px;
}

/* 与原型一致：单行输入框高度 38px（剧目简介 textarea 除外） */
.profile-edit-form >>> .ivu-input-wrapper:not(.ivu-input-type-textarea) .ivu-input {
  height: 38px;
  line-height: 20px;
  padding: 8px 10px;
}

.profile-edit-form >>> .ivu-input-type-textarea textarea.ivu-input {
  min-height: 80px;
  line-height: 1.5;
  padding: 8px 10px;
}

.profile-input-readonly {
  pointer-events: none;
  display: block;
  width: 100%;
}

.profile-input-readonly--block >>> .ivu-input-wrapper {
  width: 100%;
}

.profile-input-readonly >>> .ivu-input[disabled] {
  color: #606266;
  background-color: #f5f7fa;
  cursor: not-allowed;
}

.profile-edit-form >>> .ivu-input-wrapper:not(.ivu-input-type-textarea) .ivu-input[disabled] {
  color: #606266;
  background-color: #f5f7fa;
}

.profile-cover-list-field {
  padding-bottom: 4px;
}

.profile-cover-file-input {
  display: none;
}

.profile-edit-form >>> .profile-cover-upload-btn.ivu-btn {
  margin-top: 10px;
  color: #2d8cf0;
  border-color: #2d8cf0;
  background-color: transparent;
  box-shadow: none;
}

.profile-edit-form >>> .profile-cover-upload-btn.ivu-btn:hover,
.profile-edit-form >>> .profile-cover-upload-btn.ivu-btn:focus {
  color: #57a3f3;
  border-color: #57a3f3;
  background-color: transparent;
}

.profile-edit-form {
  padding-bottom: 8px;
}
</style>

<style>
/*
 * 【临时】弹窗垂直居中（配合 script 中 syncModalVerticalCenter 等）
 * 不需要时可删除本段至 .view-profile-dialog .ivu-modal 之前，并移除 script 内对应逻辑。
 */
.ivu-modal-wrap.view-profile-dialog {
  overflow-y: auto;
}

.ivu-modal-wrap.view-profile-dialog .ivu-modal {
  margin-left: auto;
  margin-right: auto;
}

/* 查看态固定高度，纯 CSS 垂直居中（覆盖 iView 默认 top，且不做过渡） */
.ivu-modal-wrap.view-profile-dialog:not(.view-profile-dialog--edit) .ivu-modal {
  top: max(24px, calc((100vh - 733px) / 2)) !important;
  transition: none;
}

.view-profile-dialog .ivu-modal {
  width: 980px !important;
}

.view-profile-dialog .ivu-modal-content {
  width: 980px !important;
}

.view-profile-dialog .ivu-modal-header {
  height: 77px;
  padding: 0 24px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  border-bottom: 1px solid #e8eaec;
}

.view-profile-dialog .ivu-modal-header-inner {
  box-sizing: border-box;
  flex: 1;
  min-width: 0;
  max-width: 100%;
  padding-right: 48px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  font-family: Arial, 'Microsoft YaHei', sans-serif;
  font-size: 18px;
  font-weight: 600;
  color: #263f58;
  line-height: 24px;
}

.view-profile-dialog .ivu-modal-body {
  height: 590px;
  padding: 16px 24px 20px;
  box-sizing: border-box;
  overflow: hidden;
  display: flex;
  flex-direction: column;
}

/* 编辑 · 剧目信息：body 高度贴近原型（约表单 + Tabs 栏 + padding） */
.view-profile-dialog--edit.view-profile-dialog--edit-info-tab .ivu-modal-body {
  height: auto;
  min-height: 0;
  padding-bottom: 16px;
  overflow: visible;
}

.view-profile-dialog--edit.view-profile-dialog--edit-info-tab .profile-dialog-body-wrap {
  flex: 0 0 auto;
  height: auto;
}

/* 编辑 · 单集列表：固定 body，列表区撑满 */
.view-profile-dialog--edit.view-profile-dialog--edit-episodes-tab .ivu-modal-body {
  height: 728px;
  overflow: hidden;
}

.view-profile-dialog .ivu-modal-body .profile-dialog-body-wrap {
  flex: 1;
  min-height: 0;
}

.view-profile-dialog .ivu-modal-footer {
  height: 66px;
  padding: 0 24px;
  box-sizing: border-box;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  border-top: 1px solid #e8eaec;
}

.view-profile-dialog .ivu-modal-footer .ivu-btn + .ivu-btn {
  margin-left: 10px;
}

.view-profile-dialog .ivu-modal-close {
  top: 22px;
  right: 20px;
  z-index: 1;
}

/*
 * 查看态 · 剧目信息 Tab：中间区域固定高度，.profile-info-panel 内滚动（滚动条不贴 footer）
 */
.view-profile-dialog:not(.view-profile-dialog--edit)
  .profile-dialog-body-wrap--info-tab
  .profile-tabs-panel:not(.profile-tabs-panel--episodes) {
  display: flex;
  flex-direction: column;
  height: min(calc(590px - 36px - 72px - 48px), calc(100vh - 240px)) !important;
  max-height: min(calc(590px - 36px - 72px - 48px), calc(100vh - 240px));
  overflow: hidden;
  box-sizing: border-box;
}

.view-profile-dialog:not(.view-profile-dialog--edit)
  .profile-dialog-body-wrap--info-tab.profile-dialog-body-wrap--has-reject
  .profile-tabs-panel:not(.profile-tabs-panel--episodes) {
  height: min(calc(590px - 36px - 72px - 48px - 64px), calc(100vh - 280px)) !important;
  max-height: min(calc(590px - 36px - 72px - 48px - 64px), calc(100vh - 280px));
}

.view-profile-dialog:not(.view-profile-dialog--edit)
  .profile-dialog-body-wrap--info-tab
  .profile-info-panel {
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}

/*
 * 单集 Tab：显式高度（避免 height:100% 在 iView Tabs 下塌成 0 白屏），超出在 .episode-list-wrap 滚动
 */
.view-profile-dialog:not(.view-profile-dialog--edit) .profile-tabs-panel--episodes {
  height: min(calc(590px - 36px - 72px - 48px), calc(100vh - 240px)) !important;
  max-height: min(calc(590px - 36px - 72px - 48px), calc(100vh - 240px));
}

.view-profile-dialog:not(.view-profile-dialog--edit)
  .profile-dialog-body-wrap--has-reject
  .profile-tabs-panel--episodes {
  height: min(calc(590px - 36px - 72px - 48px - 64px), calc(100vh - 280px)) !important;
  max-height: min(calc(590px - 36px - 72px - 48px - 64px), calc(100vh - 280px));
}

.view-profile-dialog.view-profile-dialog--edit.view-profile-dialog--edit-episodes-tab
  .profile-tabs-panel--episodes {
  height: min(calc(728px - 36px - 72px), calc(100vh - 220px)) !important;
  max-height: min(calc(728px - 36px - 72px), calc(100vh - 220px));
}

.view-profile-dialog.view-profile-dialog--edit.view-profile-dialog--edit-episodes-tab
  .profile-dialog-body-wrap--has-reject
  .profile-tabs-panel--episodes {
  height: min(calc(728px - 36px - 72px - 96px), calc(100vh - 260px)) !important;
  max-height: min(calc(728px - 36px - 72px - 96px), calc(100vh - 260px));
}

.view-profile-dialog .profile-tabs-panel--episodes .episode-list-wrap {
  flex: 1 1 auto;
  min-height: 0;
  overflow-x: hidden;
  overflow-y: auto;
  -webkit-overflow-scrolling: touch;
}
</style>
